import { GoPrimitiveDot, GoSettings } from "react-icons/go";
import "react-calendar/dist/Calendar.css";
import CalendarComp from "../../../components/Common/CalendarComp";
import { ButtonComp, CardComp, TableCompData } from "../../../components";
import { AttendanceHeader } from "../../../utils/TableHeader";
import FilterFind from "../../../components/FilterFind";
import { useGetAttendanceStatusesQuery } from "../../../store/selectableSlice";
import EmptyTable from "../../../components/Common/EmptyTable";
import useAdminAttendanceActions from "./useAdminAttendanceActions";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { dateFromString } from "../../../utils";
import { Form } from "react-bootstrap";
import TablePagination from "../../../components/Table/Pagination";

import { ModalCompAdvance } from "../../../components/Ui/ModalComp";
import { formatMoney } from "../../../utils/currencyFormat";
import { useGetFinesQuery } from "../../../store/admin/settings/fineSlice";
import { resolveAbsenceFine } from "../../../utils/fineUtils";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { useGetUnmarkedMembersAttendanceQuery, useGetMarkedMembersByDateQuery, useGetBranchAttendanceSummaryQuery } from "../../../store/admin/attendanceSlice";
import AttendanceStatusChangeModal from "./modules/AttendanceStatusChangeModal";
import { BsPencilSquare, BsWallet2, BsCollection } from "react-icons/bs";
import moment from "moment";

export default function AttendanceHome() {
  const navigate = useNavigate();
  const { newAttendance, ismarkingAttendance } = useAdminAttendanceActions();

  const { data: attendance_statuses = [] } = useGetAttendanceStatusesQuery();
  const { data: finesDataResponse } = useGetFinesQuery();
  const { data: branchSummary, isLoading: isLoadingSummary } = useGetBranchAttendanceSummaryQuery();
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [pageNumber, setPageNumber] = useState(1);
  const [markedPageNumber, setMarkedPageNumber] = useState(1);
  const perPage = 20;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fineImpactSummary, setFineImpactSummary] = useState({ count: 0, total: 0, perPerson: 0 });

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [activeTab, setActiveTab] = useState("unmarked"); // "unmarked" or "marked"

  // Extract fines array safely
  const finesList = useMemo(() => {
    if (Array.isArray(finesDataResponse)) return finesDataResponse;
    if (finesDataResponse?.data && Array.isArray(finesDataResponse.data)) return finesDataResponse.data;
    if (finesDataResponse?.fines && Array.isArray(finesDataResponse.fines)) return finesDataResponse.fines;
    return [];
  }, [finesDataResponse]);

  // Resolve absence/attendance fine (category "Attendance" or "Absence", or name fallback; active only)
  const { amount: attendanceFineAmount, statusChangeWindow } = useMemo(
    () => resolveAbsenceFine(finesList),
    [finesList]
  );
  
  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate > today;
  };

  const todayDate = dateFromString(selectedDate, true);
  const isDateValid = !isFutureDate(selectedDate);
  
  const {
    data: unmarkedAttendance = {},
    isFetching: isFetchingAttendance,
    isLoading: isLoadingAttendance,
    error,
  } = useGetUnmarkedMembersAttendanceQuery(
    {
      page: pageNumber,
      limit: perPage,
      date: todayDate,
    },
    {
      skip: !isDateValid || !todayDate,
    }
  );

  // Query for ALREADY marked members for this date
  const {
    data: markedResponse = {},
    isFetching: isFetchingMarked,
  } = useGetMarkedMembersByDateQuery(
    {
      date: todayDate,
      page: markedPageNumber,
      limit: 10, // Show fewer marked members by default
    },
    {
      skip: !isDateValid || !todayDate,
    }
  );

  const { allMembers = [] } = useMemo(() => {
    if (!unmarkedAttendance || !isDateValid) {
      return { allMembers: [] };
    }
    
    let company_attendances = [];
    if (Array.isArray(unmarkedAttendance)) {
      company_attendances = unmarkedAttendance;
    } else if (unmarkedAttendance?.company_attendances) {
      company_attendances = unmarkedAttendance.company_attendances;
    } else if (unmarkedAttendance?.data?.company_attendances) {
      company_attendances = unmarkedAttendance.data.company_attendances;
    } else if (unmarkedAttendance?.data && Array.isArray(unmarkedAttendance.data)) {
      company_attendances = unmarkedAttendance.data;
    }
    
    // const paginationData = unmarkedAttendance?.pagination || {};
    
    const formattedMembers = company_attendances.map((item, index) => {
      const memberName = (item.name || `${item.first_name || ""} ${item.last_name || ""}`).trim();
      
      const serialNumber = index + 1 + (pageNumber - 1) * perPage;
      
      return {
        name: memberName || "Unknown Member",
        photo: item.photo,
        serialNumber: serialNumber,
        id: item.id || item.member_id,
        email: item.email,
      };
    });
    return { allMembers: formattedMembers };
  }, [unmarkedAttendance, isDateValid, pageNumber, perPage]);

  // Map marked members data
  const { markedMembers = [], markedPagination = {} } = useMemo(() => {
    const list = Array.isArray(markedResponse) ? markedResponse : (markedResponse?.data || []);
    const pag = markedResponse?.pagination || {};
    
    return {
      markedMembers: list.map((item, index) => {
        // Handle different backend response structures (Resource vs Raw Model)
        const memberName = typeof item.member === 'string' 
          ? item.member 
          : (item.member?.first_name ? `${item.member.first_name} ${item.member.last_name}` : (item.name || "Unknown Member"));
          
        return {
          ...item,
          serialNumber: index + 1 + (markedPageNumber - 1) * 10,
          name: memberName || "Unknown Member",
          status_name: item.status?.name || item.status_name || "Unknown",
        };
      }),
      markedPagination: pag
    };
  }, [markedResponse, markedPageNumber]);
  
  if (error) {
    toast.error(error.data?.message || "An error occured");
  }
  if (error?.status === 401) {
    navigate("/login");
  }

  const [attendance, setAttendance] = useState([]);
  const [markAllStatus, setMarkAllStatus] = useState("");

  const handleDateChange = (date) => {
    if (isFutureDate(date)) {
      toast.error("Cannot mark attendance for future dates");
      return;
    }
    setSelectedDate(date);
    setPageNumber(1);
    setMarkedPageNumber(1);
    setAttendance([]);
    setMarkAllStatus("");
  };

  const handleMarkAll = () => {
    if (!markAllStatus) {
      toast.error("Please select an attendance status");
      return;
    }

    if (allMembers.length === 0) {
      toast.error("No members to mark");
      return;
    }

    const selectedStatus = attendance_statuses.find(s => s.id === parseInt(markAllStatus));
    const isFineStatus = String(selectedStatus?.name).toLowerCase().includes("non-approved");

    const allMembersAttendance = allMembers.map((member) => ({
      member_id: member.id,
      attendance_status_id: parseInt(markAllStatus),
    }));

    setAttendance(allMembersAttendance);
    
    if (isFineStatus) {
      toast.error(`Warning: Marking all ${allMembers.length} members as ${selectedStatus?.name} will apply fines to all.`, { duration: 5000 });
    } else {
      toast.success(`Marked all ${allMembers.length} members as ${selectedStatus?.name || 'selected status'}`);
    }
  };

  const handleInitialSubmit = () => {
    const fineStatusIds = attendance_statuses
      .filter(s => String(s.name).toLowerCase().includes("non-approved"))
      .map(s => s.id);
    
    const membersWithFines = attendance.filter(item => fineStatusIds.includes(item.attendance_status_id));
    
    if (membersWithFines.length > 0) {
      setFineImpactSummary({
        count: membersWithFines.length,
        perPerson: attendanceFineAmount,
        total: membersWithFines.length * attendanceFineAmount 
      });
      setShowConfirmModal(true);
    } else {
      submitAttendance();
    }
  };

  const submitAttendance = async () => {
    setShowConfirmModal(false);
    const result = await newAttendance({ attendance, date: todayDate });
    if (result?.success) {
      setAttendance([]);
    }
  };

  const markAttendance = (member_id, attendancePoint) => {
    setAttendance((prev) => {
      const member = prev.find(
        (item) => String(member_id) === String(item.member_id)
      );
      if (!member) {
        return [
          ...prev,
          { member_id: member_id, attendance_status_id: attendancePoint },
        ];
      }
      return prev.map((item) => {
        if (String(member_id) === String(item.member_id)) {
          return {
            member_id: member_id,
            attendance_status_id: attendancePoint,
          };
        }
        return item;
      });
    });
  };

  const handleEditMarked = (row) => {
    // Security check: Verify if status can still be changed
    const attendanceDate = moment(row.attendance_date || row.created_at);
    if (moment().diff(attendanceDate, 'days') > statusChangeWindow) {
      toast.error(`Security Lock: Attendance status cannot be changed after ${statusChangeWindow} days.`);
      return;
    }

    setSelectedEntry({
      ...row,
      status_id: row.attendance_status_id,
      status_name: row.status_name
    });
    setSelectedMember(row.member);
    setShowEditModal(true);
  };

  const markedColumns = [
    { name: "S/N", selector: (row) => row.serialNumber, width: "70px" },
    { name: "Member", selector: (row) => row.name, width: "200px" },
    { 
      name: "Status", 
      selector: (row) => (
        <span 
          className="px-2 py-1 rounded text-white small"
          style={{ background: Number(row.attendance_status_id) < 3 ? "#19BC31" : "#FF4343" }}
        >
          {row.status_name}
        </span>
      )
    },
    { 
      name: "Fine Status", 
      selector: (row) => row.fine > 0 ? (
        <span className="text-danger fw-bold">{formatMoney(row.fine)} ({row.fine_status})</span>
      ) : <span className="text-muted small">None</span>
    },
    {
      name: "Action",
      cell: (row) => {
        const attendanceDate = moment(row.attendance_date || row.created_at);
        const isLocked = moment().diff(attendanceDate, 'days') > statusChangeWindow;

        return (
          <ButtonComp
            btnText={isLocked ? "Locked" : "Change Status"}
            btnClassName={`${isLocked ? "bg-light text-muted border-0" : "bg-light text-dark border"} py-1 px-3 rounded small d-flex align-items-center gap-2`}
            icon={!isLocked && <BsPencilSquare size={14} />}
            onClick={() => !isLocked && handleEditMarked(row)}
            disabled={isLocked}
            style={{ opacity: isLocked ? 0.6 : 1 }}
          />
        );
      }
    }
  ];

  return (
    <section className="mx-lg-4 min-vh-100 pb-5 mb-5">
      <div
        className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4"
        data-tour="staff-attendance-header"
      >
        <FilterFind title={"Attendance Marking"} marginBottom="0" />
        <ButtonComp
          btnText="Fine Settings"
          btnClassName="bg-white text-primary1 border border-primary1 py-2 px-3 rounded shadow-sm d-flex align-items-center gap-2 small fw-bold"
          style={{ fontFamily: "'Work Sans', sans-serif" }}
          icon={<GoSettings size={16} />}
          onClick={() => navigate("/super_admin/settings/fine_settings")}
        />
      </div>

      {/* Branch Summary Cards */}
      <section className="row g-4 mb-5">
        <div className="col-md-6">
          <CardComp cardClassName="h-100 border-0 shadow3 py-4 px-4 rounded-4">
            <div className="d-flex align-items-center gap-4">
              <div className="bg-success bg-opacity-10 p-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                <BsWallet2 style={{ color: "#19BC31" }} size={24} />
              </div>
              <div>
                <h3 className="fw-bold mb-1" style={{ fontFamily: "'DM Sans', sans-serif", color: "#383838" }}>
                  {isLoadingSummary ? "..." : formatMoney(branchSummary?.totalFinesCollected || 0)}
                </h3>
                <p className="text-muted small mb-0 fw-medium" style={{ fontFamily: "'Work Sans', sans-serif" }}>Total Fines Collected</p>
              </div>
            </div>
          </CardComp>
        </div>
        <div className="col-md-6">
          <CardComp cardClassName="h-100 border-0 shadow3 py-4 px-4 rounded-4">
            <div className="d-flex align-items-center gap-4">
              <div className="bg-warning bg-opacity-10 p-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                <BsCollection style={{ color: "#FFA500" }} size={24} />
              </div>
              <div>
                <h3 className="fw-bold mb-1" style={{ fontFamily: "'DM Sans', sans-serif", color: "#383838" }}>
                  {isLoadingSummary ? "..." : formatMoney(branchSummary?.totalPendingFines || 0)}
                </h3>
                <p className="text-muted small mb-0 fw-medium" style={{ fontFamily: "'Work Sans', sans-serif" }}>Total Pending Fines</p>
              </div>
            </div>
          </CardComp>
        </div>
      </section>

      <CardComp>
        <div className="py-4"></div>

        <div className="row g-5 mb-5" data-tour="staff-attendance-calendar">
          <div className="col-lg-5">
            <CalendarComp
              value={selectedDate}
              onChange={handleDateChange}
              maxDate={new Date()}
            />
          </div>
          <div className="col-lg-7">
            <div className="ms-lg-5">
              <div className="p-4 rounded-4 shadow">
                <h6 className="mb-4 fw-3">Marking Instructions</h6>
                <div className="d-flex align-items-start mb-3">
                  <GoPrimitiveDot color="var(--color-primary)" className="me-3 mt-1" />
                  <p className="mb-0 small">
                    Select the date from the calendar to view members who haven't been marked for that day.
                  </p>
                </div>
                <div className="d-flex align-items-start mb-3">
                  <GoPrimitiveDot color="var(--color-primary)" className="me-3 mt-1" />
                  <p className="mb-0 small">
                    Use the status columns to mark each member individually, or use the "Mark all as" feature for bulk actions.
                  </p>
                </div>
                <div className="d-flex align-items-start">
                  <GoPrimitiveDot color="var(--color-primary)" className="me-3 mt-1" />
                  <p className="mb-0 small">
                    <strong className="text-danger">Important:</strong> Marking a member as <strong>Non-Approved Absent</strong> will automatically trigger a fine of <strong>{formatMoney(attendanceFineAmount)}</strong> according to your cooperative's settings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABS NAVIGATION */}
        <div data-tour="staff-attendance-marking">
        <div className="d-flex border-bottom mb-4">
          <button
            className={`px-4 py-3 border-0 bg-transparent fw-bold ${activeTab === "unmarked" ? "border-bottom border-primary1 text-primary1" : "text-muted"}`}
            onClick={() => setActiveTab("unmarked")}
            style={{ 
              borderBottom: activeTab === "unmarked" ? "3px solid var(--color-primary)" : "none",
              marginBottom: "-1px"
            }}
          >
            Pending Marking ({unmarkedAttendance?.pagination?.total || allMembers.length})
          </button>
          <button
            className={`px-4 py-3 border-0 bg-transparent fw-bold ${activeTab === "marked" ? "border-bottom border-primary1 text-primary1" : "text-muted"}`}
            onClick={() => setActiveTab("marked")}
            style={{ 
              borderBottom: activeTab === "marked" ? "3px solid var(--color-primary)" : "none",
              marginBottom: "-1px"
            }}
          >
            Already Marked ({markedResponse?.pagination?.total || markedMembers.length})
          </button>
        </div>

        {activeTab === "unmarked" ? (
          <div id="unmarked-section">
            <div className="mb-4">
              <h5 className="fw-bold text-black1">Unmarked Members</h5>
              <p className="text-muted small">Members waiting to be marked for {dateFromString(selectedDate)}</p>
            </div>

            {allMembers.length > 0 && (
              <div className="mb-3 d-flex align-items-center gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <Form.Select
                    value={markAllStatus}
                    onChange={(e) => setMarkAllStatus(e.target.value)}
                    className="py-2"
                    style={{ minWidth: "250px" }}
                  >
                    <option value="">Select status to mark all</option>
                    {attendance_statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </Form.Select>
                  <ButtonComp
                    btnText="Mark all as"
                    btnClassName="px-4 py-2 bg-primary1 text-white rounded"
                    onClick={handleMarkAll}
                    disabled={!markAllStatus || isFutureDate(selectedDate)}
                  />
                </div>
                <small className="text-muted">
                  You can still change individual selections before submitting
                </small>
              </div>
            )}
            
            <div className="mb-5">
              <TableCompData
                columns={AttendanceHeader(
                  markAttendance,
                  attendance_statuses,
                  attendance
                )}
                data={allMembers}
                marginBottom={"10px"}
                cellPadding={"0px"}
                progressPending={isFetchingAttendance || isLoadingAttendance}
                progressComponent={<TableSkeletonLoader rows={10} columns={5} />}
                message={
                  !isFetchingAttendance && !isLoadingAttendance && !isDateValid ? (
                    <EmptyTable Message={"Cannot mark attendance for future dates"} />
                  ) : !isFetchingAttendance && !isLoadingAttendance && allMembers.length === 0 ? (
                    <EmptyTable Message={`All members marked for ${dateFromString(selectedDate)}`} />
                  ) : null
                }
              />
              {!isFetchingAttendance && !isLoadingAttendance && allMembers.length > 0 && (
                <div className="text-start mt-3">
                  <ButtonComp
                    btnText={"Submit attendance"}
                    btnClassName={"px-5 py-3 h6 text-white bg-primary1 rounded"}
                    onClick={handleInitialSubmit}
                    disabled={ismarkingAttendance || attendance.length === 0}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div id="marked-section">
            <div className="mb-4">
              <h5 className="fw-bold text-black1">Already Marked</h5>
              <p className="text-muted small">Members who have already been recorded for this date. You can update their status below.</p>
            </div>
            <TableCompData
              columns={markedColumns}
              data={markedMembers}
              marginBottom={"10px"}
              progressPending={isFetchingMarked}
              progressComponent={<TableSkeletonLoader rows={5} columns={5} />}
              message={
                !isFetchingMarked && markedMembers.length === 0 ? (
                  <EmptyTable Message={`No members have been marked for ${dateFromString(selectedDate)}`} />
                ) : null
              }
            />
            {markedPagination.totalPages > 1 && (
              <TablePagination
                marginTop={"1rem"}
                handlePageChange={setMarkedPageNumber}
                count={markedMembers.length}
                currentPage={markedPagination.currentPage || 1}
                perPage={10}
                total={markedPagination.total || 0}
                totalPages={markedPagination.totalPages || 1}
              />
            )}
          </div>
        )}
        </div>
      </CardComp>

      {/* Financial Impact Confirmation Modal */}
      <ModalCompAdvance
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        centered={true}
        size="md"
        close={true}
        arrayComp={[{
          component: (
            <div className="py-3">
              <div className="text-center mb-4">
                <div className="bg-danger bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                  <GoPrimitiveDot color="#FF4343" size={40} />
                </div>
                <h5 className="fw-bold">Financial Impact Confirmation</h5>
                <p className="text-muted">You are about to finalize attendance with fines.</p>
              </div>

              <div className="bg-light p-4 rounded-4 mb-4 border">
                <div className="d-flex justify-content-between mb-3 border-bottom pb-2">
                  <span className="text-muted">Members to be Fined:</span>
                  <span className="fw-bold">{fineImpactSummary.count}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Total Fine Amount (Estimated):</span>
                  <span className="fw-bold text-danger" style={{ fontSize: "1.2rem" }}>
                    {formatMoney(fineImpactSummary.total)}
                  </span>
                </div>
              </div>

              <p className="small text-muted text-center mb-4">
                By clicking confirm, fines will be generated for all members marked as "Non-Approved Absent". This action is reversible only through manual status edits.
              </p>

              <div className="d-flex gap-3">
                <ButtonComp
                  btnText="Review Selection"
                  btnClassName="w-100 py-3 rounded-3 bg-white border text-dark"
                  onClick={() => setShowConfirmModal(false)}
                />
                <ButtonComp
                  btnText="Confirm & Submit"
                  btnClassName="w-100 py-3 rounded-3 text-white bg-primary1"
                  onClick={submitAttendance}
                  loading={ismarkingAttendance}
                />
              </div>
            </div>
          )
        }]}
        modalBody={0}
      />

      {/* Edit Attendance Modal */}
      {showEditModal && selectedEntry && selectedMember && (
        <AttendanceStatusChangeModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          memberData={{
            id: selectedMember.id,
            name: selectedMember.first_name ? `${selectedMember.first_name} ${selectedMember.last_name}` : (selectedMember.name || "Unknown"),
            member_no: selectedMember.member_no,
          }}
          currentStatus={{
            id: selectedEntry.status_id,
            name: selectedEntry.status_name,
            entry_id: selectedEntry.id,
          }}
          fineData={selectedEntry.fine > 0 ? { 
            amount: selectedEntry.fine, 
            status: selectedEntry.fine_status 
          } : { 
            amount: attendanceFineAmount, 
            status: "INACTIVE" 
          }}
        />
      )}
    </section>
  );
}

